/**
 * Central query-key factory. All React Query hooks should read their keys
 * from here so invalidation lines up across features. Richer per-feature
 * factories can be added under `src/query/keys/*` later.
 */

const me: readonly ['me'] = ['me'] satisfies readonly ['me'];

function personKey(guid: string): readonly ['person', string] {
  return ['person', guid] satisfies readonly ['person', string];
}

function searchPatientsKey(
  q: string,
  limit: number,
): readonly ['search', 'patients', string, number] {
  return ['search', 'patients', q, limit] satisfies readonly [
    'search',
    'patients',
    string,
    number,
  ];
}

function recentPatientsKey(
  limit: number,
): readonly ['recent-patients', number] {
  return ['recent-patients', limit] satisfies readonly ['recent-patients', number];
}

function personFeaturesKey(
  guid: string,
): readonly ['person', string, 'features'] {
  return ['person', guid, 'features'] satisfies readonly [
    'person',
    string,
    'features',
  ];
}

function personDiagnosesKey(
  guid: string,
): readonly ['person', string, 'diagnoses'] {
  return ['person', guid, 'diagnoses'] satisfies readonly [
    'person',
    string,
    'diagnoses',
  ];
}

function personGeneticVariantsKey(
  guid: string,
): readonly ['person', string, 'genetic-info'] {
  return ['person', guid, 'genetic-info'] satisfies readonly [
    'person',
    string,
    'genetic-info',
  ];
}

function familyKey(guid: string): readonly ['family', string] {
  return ['family', guid] satisfies readonly ['family', string];
}

function recentFamiliesKey(
  limit: number,
): readonly ['recent-families', number] {
  return ['recent-families', limit] satisfies readonly ['recent-families', number];
}

function searchFamiliesKey(q: string): readonly ['search', 'families', string] {
  return ['search', 'families', q] satisfies readonly ['search', 'families', string];
}

/**
 * Prefix shared by every identifier-type cache slice; mutations invalidate
 * by this prefix so all variants (all + active-only) refresh together.
 */
const identifierTypes: readonly ['identifier-types'] = ['identifier-types'];
const identifierTypesActive: readonly ['identifier-types', 'active'] = [
  'identifier-types',
  'active',
];

/**
 * Prefix shared by every consent-type cache slice. Mutations invalidate by
 * this prefix so both the admin list and any clinician-side typeahead
 * (PTV2-1209) refresh together.
 */
const consentTypes: readonly ['consent-types'] = ['consent-types'];

function userKey(guid: string): readonly ['user', string] {
  return ['user', guid] satisfies readonly ['user', string];
}

/**
 * Staff-directory lookup (PTV2-1567). Kept apart from `user` on purpose: `/staff` returns a
 * names-only `StaffDto` and `/users` an admin `UserDto`, so they must never share a cache entry.
 */
function staffKey(guid: string): readonly ['staff', string] {
  return ['staff', guid] satisfies readonly ['staff', string];
}

/**
 * Cross-patient task inbox (PTV2-1236 My Cases). Prefix shared by every
 * assignee/status/page variant; the per-patient task mutations invalidate it
 * so completing or editing a task from the inbox refreshes the list.
 */
const myCasesTasks: readonly ['my-cases', 'tasks'] = ['my-cases', 'tasks'];

/**
 * FHIR Integrations admin (PTV2-1145 / PTV2-1473) — the identifier mappings and mapped-field
 * allow-list are per-integration-entry, so each cache slice is keyed by the owning integration guid.
 */
function fhirIdentifierMappingsKey(
  integrationGuid: string,
): readonly ['fhir-integrations', string, 'identifier-mappings'] {
  return ['fhir-integrations', integrationGuid, 'identifier-mappings'] satisfies readonly [
    'fhir-integrations',
    string,
    'identifier-mappings',
  ];
}
function fhirMappedFieldsKey(
  integrationGuid: string,
): readonly ['fhir-integrations', string, 'mapped-fields'] {
  return ['fhir-integrations', integrationGuid, 'mapped-fields'] satisfies readonly [
    'fhir-integrations',
    string,
    'mapped-fields',
  ];
}

const questionnaireTemplates: readonly ['questionnaire-templates'] = ['questionnaire-templates'];
const questionnaireDeliveryMethods: readonly ['questionnaire-delivery-methods'] = [
  'questionnaire-delivery-methods',
];
const questionnaireConfig: readonly ['questionnaire-config'] = ['questionnaire-config'];

const emailServers: readonly ['email-servers'] = ['email-servers'];

const s3Configs: readonly ['s3-configs'] = ['s3-configs'];

function templateVariablesKey(context: string): readonly ['template-variables', string] {
  return ['template-variables', context] satisfies readonly ['template-variables', string];
}

function emailServerStatusKey(id: string): readonly ['email-servers', string, 'status'] {
  return ['email-servers', id, 'status'] satisfies readonly ['email-servers', string, 'status'];
}

function personQuestionnaireAttachmentsKey(
  guid: string,
  requestGuid: string,
): readonly ['person', string, 'questionnaires', string, 'attachments'] {
  return ['person', guid, 'questionnaires', requestGuid, 'attachments'] satisfies readonly [
    'person',
    string,
    'questionnaires',
    string,
    'attachments',
  ];
}

function browseQuestionnairesKey(
  page: number,
  size: number,
  status: string | null,
  templateId: string | null,
  q: string,
  createdBy: string | null,
): readonly [
  'questionnaires',
  'browse',
  number,
  number,
  string | null,
  string | null,
  string,
  string | null,
] {
  return ['questionnaires', 'browse', page, size, status, templateId, q, createdBy] satisfies readonly [
    'questionnaires',
    'browse',
    number,
    number,
    string | null,
    string | null,
    string,
    string | null,
  ];
}

export const queryKeys = {
  me,
  identifierTypes,
  identifierTypesActive,
  consentTypes,
  fhirIdentifierMappings: fhirIdentifierMappingsKey,
  fhirMappedFields: fhirMappedFieldsKey,
  questionnaireTemplates,
  questionnaireDeliveryMethods,
  questionnaireConfig,
  emailServers,
  emailServerStatus: emailServerStatusKey,
  s3Configs,
  templateVariables: templateVariablesKey,
  browseQuestionnaires: browseQuestionnairesKey,
  user: userKey,
  staff: staffKey,
  myCasesTasks,
  person: personKey,
  searchPatients: searchPatientsKey,
  recentPatients: recentPatientsKey,
  personFeatures: personFeaturesKey,
  personDiagnoses: personDiagnosesKey,
  personGeneticVariants: personGeneticVariantsKey,
  personQuestionnaireAttachments: personQuestionnaireAttachmentsKey,
  family: familyKey,
  recentFamilies: recentFamiliesKey,
  searchFamilies: searchFamiliesKey,
};

export type QueryKeys = typeof queryKeys;
